'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronUp, 
  Zap, 
  Leaf, 
  Euro, 
  Sun,
  Droplets,
  Flame,
  TrendingUp,
  Users,
  Home,
  BarChart3,
  Download,
  X
} from 'lucide-react';
import { BackButton } from '../atomic/BackButton';
import { EnergySourceCardMobile } from '../molecular/EnergySourceCardMobile';
import { CallToActionSection } from '../molecular/CallToActionSection';
import { ExportSection } from '../molecular/ExportSection';
import { InfoTooltip } from '../atomic/Tooltip';

interface PotenzialDashboardMobileProps {
  gemeinde: string;
  onBack: () => void;
  onStatsCalculated?: (stats: { jahresPotenzial: number; anlagenGesamt: number; co2Ersparnis: number }) => void;
  onGemeindeInfoLoaded?: (info: { name: string; wappenLink?: string }) => void;
  energySupplier?: 'ikb' | 'tiwag';
  // Data props that would come from the parent
  energyData: any;
  gemeindeInfo: any;
  stats: any;
  isLoading?: boolean;
  error?: any;
}

export function PotenzialDashboardMobile({
  gemeinde,
  onBack,
  onStatsCalculated,
  onGemeindeInfoLoaded,
  energySupplier = 'ikb',
  energyData,
  gemeindeInfo,
  stats,
  isLoading = false,
  error = null
}: PotenzialDashboardMobileProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('energy');
  const [showExport, setShowExport] = useState(false);

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Transform energyData structure for mobile components
  const transformedEnergyData = {
    solar: {
      current: Array.isArray(energyData?.sonne) ? energyData.sonne.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      capacity: Array.isArray(energyData?.sonne) ? energyData.sonne.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      co2: Math.round(((Array.isArray(energyData?.sonne) ? energyData.sonne.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0) * 8760 * 0.4) / 1000),
      count: Array.isArray(energyData?.sonne) ? energyData.sonne.length : 0
    },
    hydro: {
      current: Array.isArray(energyData?.wasser) ? energyData.wasser.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      capacity: Array.isArray(energyData?.wasser) ? energyData.wasser.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      co2: Math.round(((Array.isArray(energyData?.wasser) ? energyData.wasser.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0) * 8760 * 0.4) / 1000),
      count: Array.isArray(energyData?.wasser) ? energyData.wasser.length : 0
    },
    biomass: {
      current: Array.isArray(energyData?.biomasse) ? energyData.biomasse.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      capacity: Array.isArray(energyData?.biomasse) ? energyData.biomasse.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      co2: Math.round(((Array.isArray(energyData?.biomasse) ? energyData.biomasse.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0) * 8760 * 0.4) / 1000),
      count: Array.isArray(energyData?.biomasse) ? energyData.biomasse.length : 0
    },
    biogas: {
      current: Array.isArray(energyData?.biogas) ? energyData.biogas.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      capacity: Array.isArray(energyData?.biogas) ? energyData.biogas.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0,
      co2: Math.round(((Array.isArray(energyData?.biogas) ? energyData.biogas.reduce((sum: number, item: any) => sum + (item.leistungKw || 0), 0) : 0) * 8760 * 0.4) / 1000),
      count: Array.isArray(energyData?.biogas) ? energyData.biogas.length : 0
    }
  };

  const totalInstallations = transformedEnergyData.solar.count + transformedEnergyData.hydro.count + 
                            transformedEnergyData.biomass.count + transformedEnergyData.biogas.count;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <BackButton onClick={onBack} className="mb-4" />
        <div className="bg-white rounded-xl p-6 text-center">
          <p className="text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="animate-pulse">
          <div className="h-8 w-32 bg-gray-200 rounded mb-4" />
          <div className="bg-white rounded-xl p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'overview',
      title: 'Übersicht',
      icon: <BarChart3 className="w-5 h-5" />,
      content: (
        <div className="space-y-3">
          {/* Key Metrics - 2x2 Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Potenzial</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {stats?.jahresPotenzial?.toLocaleString('de-DE') || '0'}€
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sun className="w-5 h-5 text-orange-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Anlagen</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {totalInstallations || '0'}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Leaf className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">CO₂-Ersparnis</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {stats?.co2Ersparnis?.toLocaleString('de-DE') || '0'}t
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Home className="w-5 h-5 text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Haushalte</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {gemeindeInfo?.haushalte?.toLocaleString('de-DE') || '0'}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setShowExport(true)}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => toggleSection('energy')}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Details
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'energy',
      title: 'Energiequellen',
      icon: <Zap className="w-5 h-5" />,
      badge: totalInstallations,
      content: (
        <div className="space-y-3">
          {/* Energy Source Cards */}
          <EnergySourceCardMobile
            title="Solar"
            icon={<Sun className="w-5 h-5" />}
            currentProduction={transformedEnergyData.solar.current}
            maxCapacity={transformedEnergyData.solar.capacity}
            co2Savings={transformedEnergyData.solar.co2}
            color="bg-yellow-500"
          />
          <EnergySourceCardMobile
            title="Wasser"
            icon={<Droplets className="w-5 h-5" />}
            currentProduction={transformedEnergyData.hydro.current}
            maxCapacity={transformedEnergyData.hydro.capacity}
            co2Savings={transformedEnergyData.hydro.co2}
            color="bg-blue-500"
          />
          <EnergySourceCardMobile
            title="Biomasse"
            icon={<Leaf className="w-5 h-5" />}
            currentProduction={transformedEnergyData.biomass.current}
            maxCapacity={transformedEnergyData.biomass.capacity}
            co2Savings={transformedEnergyData.biomass.co2}
            color="bg-green-500"
          />
          <EnergySourceCardMobile
            title="Biogas"
            icon={<Flame className="w-5 h-5" />}
            currentProduction={transformedEnergyData.biogas.current}
            maxCapacity={transformedEnergyData.biogas.capacity}
            co2Savings={transformedEnergyData.biogas.co2}
            color="bg-orange-500"
          />
        </div>
      )
    },
    {
      id: 'community',
      title: 'Bürgerbeteiligung',
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">So profitieren Sie:</h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Direkter Bezug von lokalem Ökostrom</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Langfristig stabile Strompreise</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Stärkung der regionalen Wirtschaft</span>
              </li>
            </ul>
          </div>
          
          {/* Simplified Calculator */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Ihr Sparpotenzial</h4>
            <div className="text-2xl font-bold text-green-600 mb-1">
              ~ 250€ / Jahr
            </div>
            <p className="text-xs text-gray-500">
              Bei durchschnittlichem Haushalt (3.500 kWh/Jahr)
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <BackButton onClick={onBack} />
            <div className="text-center flex-1 mx-4">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                {gemeindeInfo?.name || gemeinde}
              </h1>
              <p className="text-xs text-gray-500">Energiepotenzial</p>
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 pb-20">
        {/* Accordion Sections */}
        <div className="space-y-3">
          {sections.map((section) => (
            <motion.div
              key={section.id}
              initial={false}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="text-gray-600 dark:text-gray-400">
                    {React.cloneElement(section.icon as React.ReactElement, { className: 'w-6 h-6' } as React.HTMLAttributes<SVGElement>)}
                  </div>
                  <span className="font-semibold text-lg text-gray-900 dark:text-white">
                    {section.title}
                  </span>
                  {section.badge && (
                    <span className="bg-blue-100 text-blue-600 text-sm px-3 py-1 rounded-full font-medium">
                      {section.badge}
                    </span>
                  )}
                </div>
                {expandedSection === section.id ? (
                  <ChevronUp className="w-6 h-6 text-gray-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-400" />
                )}
              </button>
              
              <AnimatePresence>
                {expandedSection === section.id && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                      <div className="pt-4">
                        {section.content}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Section - Always visible at bottom */}
        <div className="mt-6">
          <CallToActionSection gemeindeName={gemeindeInfo?.name} />
        </div>
      </div>

      {/* Export Modal */}
      <AnimatePresence>
        {showExport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowExport(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white dark:bg-gray-800 w-full rounded-t-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Export Options</h3>
                <button onClick={() => setShowExport(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ExportSection 
                gemeindeName={gemeindeInfo?.name || ''}
                data={{
                  jahresPotenzial: stats?.jahresPotenzial || 0,
                  einwohner: gemeindeInfo?.einwohner || 0,
                  haushalte: gemeindeInfo?.haushalte || 0,
                  energieQuellen: [
                    { name: 'Solar', anzahl: transformedEnergyData.solar.count, kwh: transformedEnergyData.solar.current * 1000, potentialEuro: transformedEnergyData.solar.current * 42 },
                    { name: 'Wasser', anzahl: transformedEnergyData.hydro.count, kwh: transformedEnergyData.hydro.current * 1000, potentialEuro: transformedEnergyData.hydro.current * 42 },
                    { name: 'Biomasse', anzahl: transformedEnergyData.biomass.count, kwh: transformedEnergyData.biomass.current * 1000, potentialEuro: transformedEnergyData.biomass.current * 42 },
                    { name: 'Biogas', anzahl: transformedEnergyData.biogas.count, kwh: transformedEnergyData.biogas.current * 1000, potentialEuro: transformedEnergyData.biogas.current * 42 }
                  ]
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}